import React, { useEffect, useState } from "react";
import "@/App.css";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CookieBanner from "@/components/CookieBanner";
import PageTransition from "@/components/PageTransition";
import ScrollProgress from "@/components/ScrollProgress";
import HomePage from "@/pages/HomePage";
import ProductsPage from "@/pages/ProductsPage";
import ProductDetailPage from "@/pages/ProductDetailPage";
import IndustriesPage from "@/pages/IndustriesPage";
import IndustryDetailPage from "@/pages/IndustryDetailPage";
import PricingPage, { PRICING_FAQS, PRODUCT_PRICING } from "@/pages/PricingPage";
import ContactPage from "@/pages/ContactPage";
import LegalPage from "@/pages/LegalPage";
import { CaseStudiesIndexPage, CaseStudyPage } from "@/pages/CaseStudyPage";
import ComparePage from "@/pages/ComparePage";
import CompareDetailPage from "@/pages/CompareDetailPage";
import IntegrationsPage from "@/pages/IntegrationsPage";
import { BlogIndexPage, BlogPostPage } from "@/pages/BlogPage";
import AboutPage from "@/pages/AboutPage";
import ChangelogPage from "@/pages/ChangelogPage";
import AdminLeadsPage from "@/pages/AdminLeadsPage";
import LaunchDeckPage from "@/pages/LaunchDeckPage";
import { getProduct } from "@/lib/products";
import { getComparison } from "@/lib/comparisons";
import { getBlogPost as getPost } from "@/lib/blog";

const SITE_ORIGIN = "https://scitus.ai";

const TOP_LEVEL = [
    "home",
    "products",
    "industries",
    "pricing",
    "contact",
    "about",
    "privacy",
    "terms",
    "dpa",
    "compare",
    "integrations",
    "stories",
    "blog",
    "changelog",
    "admin-leads",
    "launch-deck",
];

// Old → new slug redirects (for product renames that already had links in the wild)
const SLUG_REDIRECTS = {
    products: { nexus: "vexen", pulse: "mativex" },
};

// Until Emergent hosting auto-resolves /path → /path/index.html, we append
// the explicit suffix to canonical URLs, og:url, BreadcrumbList items, and
// JSON-LD references. Crawlers fetch these directly and get the real
// prerendered HTML. Set to "" once hosting supports clean URLs.
const CANONICAL_SUFFIX = "/index.html";
const buildUrl = (path) => {
    if (!path || path === "/") return `${SITE_ORIGIN}/`;
    // Strip trailing slash for clean joins
    const clean = path.replace(/\/+$/, "");
    return `${SITE_ORIGIN}${clean}${CANONICAL_SUFFIX}`;
};

// Custom event our nav helpers dispatch when the path changes via pushState
// (the browser does not fire popstate for pushState — only for back/forward).
const NAV_EVENT = "scitus:navigate";

// Convert legacy hash URLs to path URLs. A first-time visitor landing on an
// older shared `/products/mativex` link gets transparently redirected to the
// canonical path `/products/mativex` — preserving every existing inbound link
// and keeping our SEO scoring intact during the migration window.
function migrateHashToPath() {
    if (typeof window === "undefined") return;
    const h = window.location.hash || "";
    if (!h || h === "#" || h === "#home") return;
    const stripped = h.replace(/^#\/?/, "").trim();
    if (!stripped) return;
    const newPath = `/${stripped}`;
    try {
        window.history.replaceState(null, "", newPath + window.location.search);
    } catch (e) { /* no-op */ }
}
migrateHashToPath();

function getPageFromPath() {
    const raw = (window.location.pathname || "/").replace(/^\/+|\/+$/g, "");
    if (!raw) return { page: "home", slug: null };
    let [base, slug] = raw.split("/");
    if (base && slug && SLUG_REDIRECTS[base] && SLUG_REDIRECTS[base][slug]) {
        slug = SLUG_REDIRECTS[base][slug];
        try { window.history.replaceState(null, "", `/${base}/${slug}`); } catch (e) { /* no-op */ }
    }
    if (base === "products" && slug) return { page: "product-detail", slug };
    if (base === "industries" && slug) return { page: "industry-detail", slug };
    if (base === "stories" && slug) return { page: "case-study", slug };
    if (base === "blog" && slug) return { page: "blog-post", slug };
    if (base === "compare" && slug) return { page: "compare-detail", slug };
    if (base === "admin" && slug === "leads") return { page: "admin-leads", slug: null };
    if (TOP_LEVEL.includes(base)) return { page: base, slug: null };
    return { page: "home", slug: null };
}

const PAGE_META = {
    home: { title: "Scitus.ai — Built individually. Better together.", desc: "Standalone products that share one platform — identity, data, and billing. Bundle 2+ and save 25%.", keywords: "SME software India, business operating platform, retail SaaS, real estate SaaS, GST billing, e-signing, HR software, procurement intelligence, Scitus, Vyastics" },
    products: { title: "Products · Scitus.ai", desc: "Standalone operator products that share one platform — identity, data, and billing. Pick what you need; bundle 2+ and save 25%.", keywords: "Scitus products, SME software suite, operator software India, business platform" },
    industries: { title: "Industries · Scitus.ai", desc: "Pre-built dashboards and workflows for retail, real estate, healthcare, manufacturing and services.", keywords: "retail software, real estate software, healthcare SaaS, manufacturing software, services SaaS India" },
    pricing: { title: "Pricing · Scitus.ai — Transparent per-product pricing for operators", desc: "Seven operator products. Transparent per-unit pricing. Mativex scales per store, Vexen per active project — each with explicit Starter, Growth and Enterprise tiers. Karya, Vyaahar, Attestra, Acrium and Kretara on flat per-unit pricing. Bundle two or more products and save 25%. Pay annually and save 20% more. Onboarding waived on annual commitments.", keywords: "SaaS pricing India, SME software pricing, business intelligence pricing, procurement software cost, HR software pricing India, Scitus pricing, Mativex price per store, Vexen price per project, volume tier pricing" },
    contact: { title: "Book a Demo · Scitus.ai", desc: "Tell us about your business — we'll tailor a 30-minute Scitus.ai demo to the products and industry that matter to you.", keywords: "Scitus demo, book a demo, contact Scitus, hello@scitus.ai" },
    about: { title: "About · Scitus.ai — built by Vyastics", desc: "Scitus.ai is operator software built by operators — developed by Vyastics Technologies Private Limited, with offices in Chennai, India and Austin, USA. Seven standalone products that share one platform.", keywords: "Vyastics Technologies, Scitus.ai about, operator software India, Chennai SaaS company" },
    privacy: { title: "Privacy · Scitus.ai", desc: "How Scitus.ai handles your data." },
    terms: { title: "Terms · Scitus.ai", desc: "Terms governing the Scitus.ai marketing site." },
    dpa: { title: "DPA · Scitus.ai", desc: "Scitus.ai Data Processing Addendum summary." },
    compare: { title: "Scitus.ai vs. alternatives · Compare", desc: "Side-by-side comparison: Scitus.ai vs. spreadsheets vs. stitched point solutions.", keywords: "Scitus vs Zoho, Scitus vs Tally, Scitus vs SAP, SME software comparison India" },
    integrations: { title: "Integrations · Scitus.ai", desc: "Connects to POS, accounting, banking, comms, and cloud storage you already use.", keywords: "Scitus integrations, Tally integration, Zoho integration, GST integration India" },
    stories: { title: "Customer stories · Scitus.ai", desc: "How operators use Scitus.ai to ship cleaner data, faster decisions, and fewer fortnight-of-weekends.", keywords: "Scitus customer stories, SME case studies India" },
    blog: { title: "Insights · Scitus.ai", desc: "Operator field notes. Patterns we see across hundreds of growing businesses.", keywords: "SME operator blog, business platform insights, Scitus blog" },
    "product-detail": { title: "Product · Scitus.ai", desc: "Deep-dive into one of the five Scitus.ai products." },
    "industry-detail": { title: "Industry · Scitus.ai", desc: "Industry-specific solutions on Scitus.ai." },
    "case-study": { title: "Customer story · Scitus.ai", desc: "How an operator team uses Scitus.ai." },
    "blog-post": { title: "Insight · Scitus.ai", desc: "Operator insights from the Scitus.ai team." },
    "compare-detail": { title: "Compare · Scitus.ai", desc: "Head-to-head comparison vs. a specific ERP.", keywords: "Scitus vs Tally, Scitus vs Zoho Books, Scitus vs SAP, ERP comparison India" },
    changelog: { title: "Changelog · Scitus.ai", desc: "A public log of every meaningful change to Scitus — product launches, infrastructure, content, and the occasional bug fix worth bragging about.", keywords: "Scitus changelog, product updates, release notes, Scitus releases, what's new" },
    "admin-leads": { title: "Admin · Leads", desc: "Internal admin view for Scitus.ai demo requests." },
    "launch-deck": { title: "Scitus.ai — Launch Deck (Confidential)", desc: "Pre-launch pitch deck. For the closed circle." },
};

function App() {
    const [{ page, slug }, setState] = useState(() => getPageFromPath());

    // Make sure the browser never restores a previous scroll position on
    // route change — we'll always start a new page at the top ourselves.
    useEffect(() => {
        if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
            window.history.scrollRestoration = "manual";
        }
    }, []);

    // Listen for both natural popstate (back/forward) AND our custom nav
    // event dispatched whenever we pushState from an in-app link click.
    useEffect(() => {
        const onNav = () => setState(getPageFromPath());
        window.addEventListener("popstate", onNav);
        window.addEventListener(NAV_EVENT, onNav);
        return () => {
            window.removeEventListener("popstate", onNav);
            window.removeEventListener(NAV_EVENT, onNav);
        };
    }, []);

    // Intercept clicks on internal absolute-path anchors so they navigate via
    // the History API instead of triggering a full page reload. External
    // links, mailto:, and anchors with explicit target="_blank" pass through
    // untouched. Modifier-clicks (cmd, ctrl, shift) and middle-click also
    // pass through so "open in new tab" still works.
    useEffect(() => {
        const onClick = (e) => {
            if (e.defaultPrevented) return;
            if (e.button !== 0) return;
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
            const a = e.target.closest && e.target.closest("a");
            if (!a) return;
            const href = a.getAttribute("href");
            if (!href) return;
            // Skip anything that isn't a relative-to-root absolute path.
            if (!href.startsWith("/")) return;
            // Same-page anchor jumps like /#products-strip are still in-page —
            // let the browser handle them.
            if (href.includes("#") && href.split("#")[0] === window.location.pathname) return;
            // Allow target=_blank, download links, etc.
            if (a.target && a.target !== "" && a.target !== "_self") return;
            if (a.hasAttribute("download")) return;
            e.preventDefault();
            const [pathPart, hashPart] = href.split("#");
            if (pathPart !== window.location.pathname) {
                window.history.pushState(null, "", href);
                window.dispatchEvent(new Event(NAV_EVENT));
            }
            // If the href has a #fragment, scroll to it after the new page renders.
            if (hashPart) {
                setTimeout(() => {
                    const el = document.getElementById(hashPart);
                    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                }, 220);
            }
        };
        document.addEventListener("click", onClick);
        return () => document.removeEventListener("click", onClick);
    }, []);

    // Reset scroll AFTER React has committed the new page. We fire multiple
    // resets covering: (1) the React commit (rAF), (2) the post-paint frame
    // (double rAF), and (3) after the PageTransition + entrance animations
    // would have finished (~250ms). This wins against every mobile-browser
    // quirk we've seen: iOS Safari clamping, Android Chrome re-layouting
    // when images decode, and intermediate FadeUp transforms.
    useEffect(() => {
        const scrollTop = () => {
            try { window.scrollTo(0, 0); } catch (_) { /* no-op */ }
            if (document.documentElement) document.documentElement.scrollTop = 0;
            if (document.body) document.body.scrollTop = 0;
        };
        const r1 = window.requestAnimationFrame(() => {
            window.requestAnimationFrame(scrollTop);
        });
        const t = window.setTimeout(scrollTop, 260);
        return () => {
            window.cancelAnimationFrame(r1);
            window.clearTimeout(t);
        };
    }, [page, slug]);

    useEffect(() => {
        const meta = PAGE_META[page] || PAGE_META.home;
        // For product detail pages, prefer the product's own copy so titles/descriptions are unique.
        let title = meta.title;
        let desc = meta.desc;
        let keywords = meta.keywords || null;
        let canonicalPath = "/";
        let ogImage = `${SITE_ORIGIN}/og-image-square.png`;
        let breadcrumbItems = [{ name: "Home", url: `${SITE_ORIGIN}/` }];
        const extraLd = [];
        let productLd = null;
        if (page === "home") {
            // FAQPage JSON-LD for the visible homepage FAQ — boosts AI-answer
            // citation rates and Google rich results.
            extraLd.push({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                url: `${SITE_ORIGIN}/`,
                mainEntity: [
                    {
                        "@type": "Question",
                        name: "Do I need to rip out my existing ERP to use Scitus?",
                        acceptedAnswer: { "@type": "Answer", text: "No — this is by design. Scitus sits on top of your existing ERP (Tally, SAP, Oracle, or custom). We do not replace your ERP. We replace the spreadsheets your team builds around it. Your ERP records transactions. Scitus tells you what they mean." },
                    },
                    {
                        "@type": "Question",
                        name: "How long does implementation actually take?",
                        acceptedAnswer: { "@type": "Answer", text: "Most teams are running their first Scitus product on real transactions within 7–14 days. Full suite rollouts (Retail Suite, Real Estate Suite) take 30–60 days end-to-end including data migration, connector builds, training, and hypercare." },
                    },
                    {
                        "@type": "Question",
                        name: "Can I start with one product and add more later?",
                        acceptedAnswer: { "@type": "Answer", text: "Yes. Pick any single product at the listed price. When you add a second product, the 25% bundle discount applies automatically to everything on your account from that point forward." },
                    },
                    {
                        "@type": "Question",
                        name: "Where is our data hosted and who can see it?",
                        acceptedAnswer: { "@type": "Answer", text: "Customer data lives in your own logical workspace on AWS Mumbai by default. Enterprise customers can opt into Singapore, Frankfurt, or US-East. Access is role-based at screen, action, and row level. We have a published DPA and never train models on customer data." },
                    },
                ],
            });
        }
        if (page === "pricing") {
            // FAQPage JSON-LD for the visible /pricing FAQ block — sourced
            // directly from PRICING_FAQS so visible copy and structured data
            // stay in lockstep.
            extraLd.push({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                url: `${SITE_ORIGIN}/pricing`,
                mainEntity: PRICING_FAQS.map((f) => ({
                    "@type": "Question",
                    name: f.q,
                    acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
            });
        }
        if (page === "product-detail" && slug) {
            const p = getProduct(slug);
            if (p) {
                title = `${p.name} · ${p.sub} · Scitus.ai`;
                desc = p.metaDescription || (p.description.length > 158 ? `${p.description.slice(0, 155)}…` : p.description);
                if (p.metaKeywords) keywords = p.metaKeywords;
                canonicalPath = `/products/${p.slug}`;
                if (p.heroShot) ogImage = `${SITE_ORIGIN}${p.heroShot}`;
                breadcrumbItems = [
                    { name: "Home", url: `${SITE_ORIGIN}/` },
                    { name: "Products", url: buildUrl("/products") },
                    { name: p.name, url: buildUrl(`/products/${p.slug}`) },
                ];
                // Per-product Offer schema built from the canonical pricing
                // table on /pricing. Mativex and Vexen expose volume tiers —
                // we emit a Starter/Growth `Offer` with the actual numeric
                // monthly USD price, and a second `Offer` for the Enterprise
                // tier flagged as custom-quoted (PriceSpecification with a
                // description, no numeric price — schema.org allows this).
                const softwareApp = {
                    "@context": "https://schema.org",
                    "@type": "SoftwareApplication",
                    name: p.name,
                    applicationCategory: "BusinessApplication",
                    operatingSystem: "Web",
                    description: p.description,
                    url: buildUrl(`/products/${p.slug}`),
                    image: p.heroShot ? `${SITE_ORIGIN}${p.heroShot}` : `${SITE_ORIGIN}/og-image-square.png`,
                    brand: { "@type": "Brand", name: "Scitus.ai" },
                    publisher: { "@type": "Organization", name: "Scitus.ai", url: SITE_ORIGIN },
                };
                const priceCfg = PRODUCT_PRICING[p.slug];
                if (priceCfg) {
                    const unitLabel = priceCfg.unit;
                    const baseOffer = {
                        "@type": "Offer",
                        priceCurrency: "USD",
                        price: String(priceCfg.annual.USD),
                        availability: "https://schema.org/InStock",
                        url: buildUrl("/pricing"),
                        priceSpecification: {
                            "@type": "UnitPriceSpecification",
                            price: priceCfg.annual.USD,
                            priceCurrency: "USD",
                            unitText: `per ${unitLabel} per month, billed annually`,
                            referenceQuantity: { "@type": "QuantitativeValue", value: 1, unitText: unitLabel },
                        },
                    };
                    if (priceCfg.tiers && priceCfg.tiers.length) {
                        const tieredOffers = priceCfg.tiers.map((t) => (
                            t.customQuote
                                ? {
                                    "@type": "Offer",
                                    name: t.name,
                                    description: `${t.range}. Custom quoted — contact sales.`,
                                    availability: "https://schema.org/InStock",
                                    url: buildUrl("/contact"),
                                    priceSpecification: {
                                        "@type": "PriceSpecification",
                                        priceCurrency: "USD",
                                        description: "Contact for price",
                                    },
                                }
                                : {
                                    ...baseOffer,
                                    name: t.name,
                                    description: `${t.range}. ${t.note || "Per-unit rate applies."}`,
                                }
                        ));
                        softwareApp.offers = {
                            "@type": "AggregateOffer",
                            priceCurrency: "USD",
                            lowPrice: String(priceCfg.annual.USD),
                            highPrice: String(priceCfg.monthly.USD),
                            offerCount: String(tieredOffers.length),
                            url: buildUrl("/pricing"),
                            offers: tieredOffers,
                        };
                    } else {
                        softwareApp.offers = baseOffer;
                    }
                }
                extraLd.push(softwareApp);
                // FAQPage on product detail pages — boosts both Google rich
                // snippets and AI/answer-engine citation rates. Each product
                // has 4-6 FAQs in its products.js entry.
                if (p.faqs && p.faqs.length) {
                    extraLd.push({
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        url: buildUrl(`/products/${p.slug}`),
                        mainEntity: p.faqs.map((f) => ({
                            "@type": "Question",
                            name: f.q,
                            acceptedAnswer: { "@type": "Answer", text: f.a },
                        })),
                    });
                }
            }
        } else if (page === "compare-detail" && slug) {
            const cmp = getComparison(slug);
            if (cmp) {
                title = `${cmp.headline} — head-to-head comparison`;
                desc = cmp.intro.length > 158 ? `${cmp.intro.slice(0, 155)}…` : cmp.intro;
                canonicalPath = `/compare/${cmp.slug}`;
                breadcrumbItems = [
                    { name: "Home", url: `${SITE_ORIGIN}/` },
                    { name: "Compare", url: buildUrl("/compare") },
                    { name: cmp.name, url: buildUrl(`/compare/${cmp.slug}`) },
                ];
                if (cmp.faq && cmp.faq.length) {
                    extraLd.push({
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        url: buildUrl(`/compare/${cmp.slug}`),
                        mainEntity: cmp.faq.map((f) => ({
                            "@type": "Question",
                            name: f.q,
                            acceptedAnswer: { "@type": "Answer", text: f.a },
                        })),
                    });
                }
            }
        } else if (page === "blog-post" && slug) {
            const post = getPost(slug);
            if (post) {
                title = `${post.title} · Scitus.ai`;
                desc = post.excerpt && post.excerpt.length > 158 ? `${post.excerpt.slice(0, 155)}…` : (post.excerpt || meta.desc);
                canonicalPath = `/blog/${post.slug}`;
                if (post.keywords) keywords = post.keywords;
                breadcrumbItems = [
                    { name: "Home", url: `${SITE_ORIGIN}/` },
                    { name: "Insights", url: `${SITE_ORIGIN}/blog` },
                    { name: post.title, url: `${SITE_ORIGIN}/blog/${post.slug}` },
                ];
                extraLd.push({
                    "@context": "https://schema.org",
                    "@type": "BlogPosting",
                    headline: post.title,
                    description: post.excerpt || "",
                    url: buildUrl(`/blog/${post.slug}`),
                    datePublished: post.date || "2026-02-15",
                    author: { "@type": "Organization", name: "Scitus.ai", url: SITE_ORIGIN },
                    publisher: { "@type": "Organization", name: "Scitus.ai", url: SITE_ORIGIN, logo: { "@type": "ImageObject", url: `${SITE_ORIGIN}/og-image-square.png` } },
                    mainEntityOfPage: buildUrl(`/blog/${post.slug}`),
                });
            }
        } else {
            // Map known top-level pages back to hash-paths for canonical
            const HASH_FOR = {
                home: "/",
                products: "/products",
                industries: "/industries",
                pricing: "/pricing",
                contact: "/contact",
                about: "/about",
                privacy: "/privacy",
                terms: "/terms",
                dpa: "/dpa",
                compare: "/compare",
                integrations: "/integrations",
                stories: "/stories",
                blog: "/blog",
            };
            canonicalPath = HASH_FOR[page] || "/";
            if (page === "products") {
                breadcrumbItems = [
                    { name: "Home", url: `${SITE_ORIGIN}/` },
                    { name: "Products", url: buildUrl("/products") },
                ];
            } else if (page === "pricing") {
                breadcrumbItems = [
                    { name: "Home", url: `${SITE_ORIGIN}/` },
                    { name: "Pricing", url: buildUrl("/pricing") },
                ];
            } else if (page === "industries") {
                breadcrumbItems = [
                    { name: "Home", url: `${SITE_ORIGIN}/` },
                    { name: "Industries", url: buildUrl("/industries") },
                ];
            } else if (page === "about") {
                breadcrumbItems = [
                    { name: "Home", url: `${SITE_ORIGIN}/` },
                    { name: "About", url: buildUrl("/about") },
                ];
            }
        }

        const canonicalUrl = buildUrl(canonicalPath);
        document.title = title;
        const setMeta = (selector, attr, value) => {
            const el = document.querySelector(selector);
            if (el) el.setAttribute(attr, value);
        };
        setMeta('meta[name="description"]', "content", desc);
        if (keywords) {
            let kw = document.querySelector('meta[name="keywords"]');
            if (!kw) {
                kw = document.createElement("meta");
                kw.setAttribute("name", "keywords");
                document.head.appendChild(kw);
            }
            kw.setAttribute("content", keywords);
        }
        setMeta('meta[property="og:title"]', "content", title);
        setMeta('meta[property="og:description"]', "content", desc);
        setMeta('meta[property="og:url"]', "content", canonicalUrl);
        setMeta('meta[property="og:image"]', "content", ogImage);
        setMeta('meta[property="og:image:secure_url"]', "content", ogImage);
        setMeta('meta[name="twitter:title"]', "content", title);
        setMeta('meta[name="twitter:description"]', "content", desc);
        setMeta('meta[name="twitter:image"]', "content", ogImage);

        // Canonical (update or create)
        let canonical = document.querySelector('link[rel="canonical"]');
        if (!canonical) {
            canonical = document.createElement("link");
            canonical.setAttribute("rel", "canonical");
            document.head.appendChild(canonical);
        }
        canonical.setAttribute("href", canonicalUrl);

        // Per-page JSON-LD (Breadcrumb + Product) — injected into a single managed <script>
        const LD_NODE_ID = "scitus-page-ld";
        const existing = document.getElementById(LD_NODE_ID);
        if (existing) existing.remove();
        const ldGraph = [
            {
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                itemListElement: breadcrumbItems.map((b, i) => ({
                    "@type": "ListItem",
                    position: i + 1,
                    name: b.name,
                    item: b.url,
                })),
            },
        ];
        if (productLd) ldGraph.push(productLd);
        for (const ld of extraLd) ldGraph.push(ld);
        const script = document.createElement("script");
        script.id = LD_NODE_ID;
        script.type = "application/ld+json";
        script.textContent = JSON.stringify(ldGraph);
        document.head.appendChild(script);
    }, [page, slug]);

    let body;
    switch (page) {
        case "products": body = <ProductsPage />; break;
        case "product-detail": body = <ProductDetailPage slug={slug} />; break;
        case "industries": body = <IndustriesPage />; break;
        case "industry-detail": body = <IndustryDetailPage slug={slug} />; break;
        case "pricing": body = <PricingPage />; break;
        case "contact": body = <ContactPage />; break;
        case "about": body = <AboutPage />; break;
        case "privacy": body = <LegalPage variant="privacy" />; break;
        case "terms": body = <LegalPage variant="terms" />; break;
        case "dpa": body = <LegalPage variant="dpa" />; break;
        case "compare": body = <ComparePage />; break;
        case "integrations": body = <IntegrationsPage />; break;
        case "stories": body = <CaseStudiesIndexPage />; break;
        case "case-study": body = <CaseStudyPage slug={slug} />; break;
        case "blog": body = <BlogIndexPage />; break;
        case "blog-post": body = <BlogPostPage slug={slug} />; break;
        case "compare-detail": body = <CompareDetailPage slug={slug} />; break;
        case "changelog": body = <ChangelogPage />; break;
        case "admin-leads": body = <AdminLeadsPage />; break;
        case "launch-deck": body = <LaunchDeckPage />; break;
        default: body = <HomePage />;
    }

    // The launch deck is a presentation surface. Strip the site chrome so
    // the deck looks like a deck — not a page in a marketing site.
    const isChromeless = page === "launch-deck";

    return (
        <div className="App" data-page={page} data-testid={`page-${page}`}>
            {!isChromeless && <ScrollProgress />}
            {!isChromeless && <Navbar current={page} />}
            <main id="main-content">
                <PageTransition pageKey={`${page}/${slug || ""}`}>{body}</PageTransition>
            </main>
            {!isChromeless && <Footer />}
            {!isChromeless && <CookieBanner />}
        </div>
    );
}

export default App;
